import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import routes from "routes/routes";
import { useAppSelector } from "store/hooks";
import { companyMetadataSelector } from "store/selectors";

import { showErrorModal } from "helpers";
import { getErrorMessageFromResponse } from "helpers/shared/errors";
import { hasFreeTrialStarted, isSubscriptionRenewalRequired } from "helpers/subscription";
import useAuth from "hooks/useAuth";

import {
  mutationSubscriptionControllerActivateFreeTrial,
  mutationSubscriptionControllerCreateSubscription,
  mutationSubscriptionControllerPayInvoiceAndCreateSubscription,
  useQuerySubscriptionControllerGetInvoicePrice,
  useQuerySubscriptionControllerGetPaymentDetailsToken,
} from "utils/swagger_react_query";

import { showCheckoutSuccessModal } from "../modals";
import { ICheckoutFormProps, IUseSubscriptionCheckoutModalProps } from "./types";

export const useSubscriptionCheckoutModal = ({
  refetchDataCallback,
  handleClose,
  pageLoadingCallback,
}: IUseSubscriptionCheckoutModalProps) => {
  const navigate = useNavigate();
  const currentCompany = useAppSelector(companyMetadataSelector);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const { getCompanyInfo } = useAuth();
  const isFreeTrialAvailable = hasFreeTrialStarted(currentCompany);
  const isRenewalRequired = isSubscriptionRenewalRequired(currentCompany);
  const [error, setError] = useState<string | null>(null);

  const handleSetError = (error: any) => {
    const errorMessage = getErrorMessageFromResponse(error);
    setError(errorMessage);
  };

  const {
    data: paymentDetailsToken,
    isFetching: isTokenFetching,
    refetch: refetchToken,
  } = useQuerySubscriptionControllerGetPaymentDetailsToken({
    onError(error) {
      showErrorModal(error);
    },
    retry: false,
    refetchOnWindowFocus: false,
    cacheTime: 0,
  });

  const { data: unpaidInvoiceAmount, isFetching: isUnpaidInvoiceAmountFetching } =
    useQuerySubscriptionControllerGetInvoicePrice({
      enabled: isRenewalRequired,
      retry: false,
      refetchOnWindowFocus: false,
    });

  const _getCurrentCompany = async () => {
    const company = await getCompanyInfo(currentCompany?.companyId || "", true);
    return company;
  };

  const handleFreeTrialSuccessCheckout = useCallback(async () => {
    try {
      setIsActionLoading?.(true);
      pageLoadingCallback?.(true);
      await mutationSubscriptionControllerActivateFreeTrial()();
      await _getCurrentCompany();
      handleClose();
      await refetchDataCallback?.();
      navigate(routes.ADMIN_TIMESHEETS);
      showCheckoutSuccessModal();
    } catch (error) {
      handleSetError(error);
      await refetchToken();
    } finally {
      setIsActionLoading?.(false);
      pageLoadingCallback?.(false);
    }
  }, [currentCompany]);

  const handleRenewSuccessCheckout = useCallback(async () => {
    try {
      setIsActionLoading?.(true);
      await mutationSubscriptionControllerCreateSubscription()();
      await _getCurrentCompany();
      handleClose();
      await refetchDataCallback?.();
      navigate(routes.ADMIN_TIMESHEETS);
      showCheckoutSuccessModal("RENEW");
    } catch (error) {
      handleSetError(error);
      await refetchToken();
    } finally {
      setIsActionLoading?.(false);
      pageLoadingCallback?.(false);
    }
  }, [currentCompany]);

  const handlePayUnpaidInvoiceAndCreateSubscription = useCallback(async () => {
    try {
      setIsActionLoading?.(true);
      await mutationSubscriptionControllerPayInvoiceAndCreateSubscription()();
      await _getCurrentCompany();
      handleClose();
      await refetchDataCallback?.();
      navigate(routes.ADMIN_TIMESHEETS);
      showCheckoutSuccessModal("RENEW");
    } catch (error) {
      handleSetError(error);
      await refetchToken();
    } finally {
      setIsActionLoading?.(false);
      pageLoadingCallback?.(false);
    }
  }, [currentCompany]);

  const handleStartFreeTrialOrRenewSubscription = () => {
    const onSuccessCheckout = isRenewalRequired
      ? handleRenewSuccessCheckout
      : handleFreeTrialSuccessCheckout;

    onSuccessCheckout();
  };

  const checkoutFormProps: ICheckoutFormProps = {
    clientSecret: paymentDetailsToken?.paymentElementSecret || "",
    handleStartFreeTrialOrRenewSubscription,
    handlePayUnpaidInvoiceAndCreateSubscription,
    hasUnpaidInvoice: !!unpaidInvoiceAmount?.totalAmount,
    externalRequestError: error,
  };

  const onClose = () => {
    setError(null);
    handleClose();
  };

  return {
    isLoading: isActionLoading || isTokenFetching || isUnpaidInvoiceAmountFetching,
    unpaidInvoiceAmount,
    isFreeTrialAvailable,
    isLoadingExpanded: {
      isActionLoading,
      isTokenFetching,
      isUnpaidInvoiceAmountFetching,
    },
    checkoutFormProps,
    onClose,
  };
};
